import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BookOpen,
  Building2,
  Mail,
  MapPin,
  Route,
  Target,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/integrations/supabase/client';

interface LearnerManager {
  profile_id: string;
  display_name: string;
  email: string;
  job_title: string | null;
  role: 'branch_admin' | 'unit_admin' | 'unit_manager';
}

interface ReportingRecipient {
  profile_id: string;
  display_name: string | null;
  email: string;
  role: LearnerManager['role'];
  unit_id: string;
}

interface LearnerDashboardPayload {
  scope: {
    role: string;
    global_company_name: string;
    branch_name: string;
    branch_country: string | null;
    unit_name: string;
    unit_code: string | null;
    unit_description: string | null;
  };
  managers: LearnerManager[];
  metrics: {
    unit_members: number;
    available_simulations: number;
    available_pathways: number;
    completed_sessions: number;
    submitted_sessions: number;
    average_score: number;
    best_score: number;
  };
}

type EnterpriseRpcClient = {
  rpc: (
    functionName: string,
    args?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: Error | null }>;
};

const enterpriseDb = supabase as unknown as EnterpriseRpcClient;

export function EnterpriseLearnerOverview() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { activeUnitId } = useOrganisation();

  const dashboardQuery = useQuery({
    queryKey: ['enterprise-learner-dashboard', tenant?.tenant_id, activeUnitId],
    queryFn: async (): Promise<LearnerDashboardPayload> => {
      const [dashboardResult, recipientResult] = await Promise.all([
        enterpriseDb.rpc('get_enterprise_learner_dashboard', { p_unit_id: activeUnitId }),
        enterpriseDb.rpc('get_my_report_recipients'),
      ]);
      if (dashboardResult.error) throw dashboardResult.error;
      if (recipientResult.error) throw recipientResult.error;

      const dashboard = dashboardResult.data as LearnerDashboardPayload;
      const recipients = (recipientResult.data as ReportingRecipient[] | null) || [];
      const branchAdministrators = recipients
        .filter((recipient) => recipient.role === 'branch_admin' && recipient.unit_id === activeUnitId)
        .map((recipient): LearnerManager => ({
          profile_id: recipient.profile_id,
          display_name: recipient.display_name || recipient.email,
          email: recipient.email,
          job_title: null,
          role: recipient.role,
        }));
      const managers = Array.from(
        new Map(
          [...dashboard.managers, ...branchAdministrators]
            .map((manager) => [manager.profile_id, manager]),
        ).values(),
      );

      return { ...dashboard, managers };
    },
    enabled: Boolean(tenant?.tenant_id && activeUnitId),
  });

  if (!activeUnitId || dashboardQuery.isLoading) {
    return (
      <Card className="overflow-hidden">
        <CardContent className="space-y-4 p-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-12 w-full" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-20" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <Card className="border-destructive/40">
        <CardContent className="flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">Your unit details are temporarily unavailable.</p>
            <p className="text-sm text-muted-foreground">Your training remains available while we refresh your organisation context.</p>
          </div>
          <Button variant="outline" onClick={() => dashboardQuery.refetch()}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  const { scope, managers, metrics } = dashboardQuery.data;

  return (
    <Card className="overflow-hidden border-primary/20">
      <div className="h-1.5 bg-primary" />
      <CardHeader className="gap-4 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">Your unit</Badge>
            {scope.unit_code && <Badge variant="outline">{scope.unit_code}</Badge>}
          </div>
          <CardTitle className="text-2xl">{scope.unit_name}</CardTitle>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="h-4 w-4" />
              {scope.global_company_name}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {scope.branch_name}{scope.branch_country ? `, ${scope.branch_country}` : ''}
            </span>
          </div>
          {scope.unit_description && (
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              {scope.unit_description}
            </p>
          )}
        </div>
        <Button onClick={() => navigate('/simulations')} className="shrink-0">
          Continue training
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric icon={Target} label="Available simulations" value={metrics.available_simulations} />
          <Metric icon={Route} label="Learning pathways" value={metrics.available_pathways} />
          <Metric icon={BookOpen} label="Sessions completed" value={metrics.completed_sessions} />
          <Metric icon={Users} label="Learners in your unit" value={metrics.unit_members} />
        </div>

        <div className="border-t pt-5">
          <div className="mb-3">
            <h3 className="font-semibold">Your reporting contacts</h3>
            <p className="text-sm text-muted-foreground">
              These are the managers authorised to review submissions from {scope.unit_name}.
            </p>
          </div>
          {managers.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {managers.map((manager) => (
                <div
                  key={manager.profile_id}
                  className="flex min-w-0 items-center justify-between gap-4 rounded-md border bg-muted/30 p-4"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{manager.display_name}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      {manager.job_title || (
                        manager.role === 'branch_admin'
                          ? 'Branch Administrator'
                          : manager.role === 'unit_manager'
                            ? 'Unit Manager'
                            : 'Unit Administrator'
                      )}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" asChild title={`Email ${manager.display_name}`}>
                    <a href={`mailto:${manager.email}`} aria-label={`Email ${manager.display_name}`}>
                      <Mail className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              A reporting manager has not yet been assigned to this unit.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Target;
  label: string;
  value: number;
}) {
  return (
    <div className="min-w-0 rounded-md border bg-background p-4">
      <Icon className="mb-3 h-5 w-5 text-primary" />
      <p className="text-2xl font-semibold">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
